import { type ReactNode, useEffect, useRef } from 'react';
import {
  getAccessToken,
  setAccessToken,
  setAuthFailureHandler,
  setCsrfToken,
  setRefreshHandler,
} from '@/core/api/client';
import { authApi } from './authApi';
import { decodeJwtClaims } from './jwt';
import { useAuthStore } from './authStore';

/** Wires the api client's auth hooks and bootstraps the session on load. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const setStatus = useAuthStore((s) => s.setStatus);
  const setSession = useAuthStore((s) => s.setSession);
  const expireSession = useAuthStore((s) => s.expireSession);
  const clear = useAuthStore((s) => s.clear);
  const bootstrapped = useRef(false);
  const refreshInFlight = useRef<Promise<boolean> | null>(null);

  useEffect(() => {
    setAuthFailureHandler(() => {
      setAccessToken(null);
      setCsrfToken(null);
      expireSession();
    });
    setRefreshHandler(() => {
      // Single-flight: N concurrent 401s share ONE /refresh call, so the many
      // parallel dashboard requests can't each rotate the refresh token and race
      // one another into a broken chain (the "Session expired after minutes" bug).
      if (refreshInFlight.current) return refreshInFlight.current;
      refreshInFlight.current = (async () => {
        try {
          await authApi.refresh();
          return true;
        } catch {
          return false;
        } finally {
          refreshInFlight.current = null;
        }
      })();
      return refreshInFlight.current;
    });
    return () => {
      setAuthFailureHandler(null);
      setRefreshHandler(null);
    };
  }, [expireSession]);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    void (async () => {
      setStatus('loading');
      try {
        await authApi.csrf();
        // Refresh FIRST: on a cold load the access token lives only in memory and
        // is gone, so we mint a new one from the httpOnly refresh cookie before
        // hitting any guarded endpoint. This avoids the guaranteed 401 on
        // /admin/auth/permissions (which previously triggered the refresh-retry
        // cycle on every page load). A missing/expired cookie throws here and is
        // the correct "not signed in" signal.
        await authApi.refresh();
        const perms = await authApi.permissions();
        // Cold bootstrap: /refresh + /permissions don't return identity. Recover the
        // user id from the access-token JWT (`sub`); email isn't in the token, so it
        // only reappears on the next interactive login. See Phase 3I/3M notes.
        const claims = decodeJwtClaims(getAccessToken());
        setSession({
          profile: { id: claims?.sub, role: perms.role },
          permissions: perms.permissions,
          mustChangePassword: perms.must_change_password,
        });
      } catch {
        clear();
      }
    })();
  }, [setStatus, setSession, clear]);

  return <>{children}</>;
}
